/** Bloque "por equipos" de una categoría (series con líneas).
 *
 * Motor de series por equipos (Mini Copa 2026, Eduardo 2026-09-11/12): una
 * categoría Round Robin o Eliminatoria puede jugarse por equipos. El bloque
 * vive dentro del format_config_json de la categoría; el backend lo valida
 * (`tournaments/team_play.py`) y aquí solo se editan y resumen sus campos. */

import type { TeamPlayConfig, TeamPlayLineKind } from "./api";

export const LINE_KIND_LABELS: Record<TeamPlayLineKind, string> = {
  singles: "Singles",
  doubles: "Dobles",
  mixed: "Dobles mixto",
};

export const LINEUP_MODE_LABELS: Record<TeamPlayConfig["lineup_mode"], string> = {
  draw: "Sorteo automático al generar la serie",
  captain: "La captura el admin / capitán antes de la serie",
  fixed: "Fija por orden de inscripción",
};

export const TEAM_SIZE_MIN = 2;
export const TEAM_SIZE_MAX = 12;
export const LINES_MAX = 9;

/** Mini Copa: equipos de 2, dos singles + un dobles, gana con 2. */
export function defaultTeamPlay(): TeamPlayConfig {
  return {
    enabled: true,
    team_size: 2,
    lines: [{ kind: "singles" }, { kind: "singles" }, { kind: "doubles" }],
    wins_to_take_tie: 2,
    lineup_mode: "draw",
    default_score: "6-2 6-2",
    default_counts_as_played: true,
  };
}

export function teamPlayEnabled(block: TeamPlayConfig | null | undefined): block is TeamPlayConfig {
  return Boolean(block && block.enabled);
}

/** Mayoría simple: con 3 líneas se gana con 2, con 5 con 3. */
export function majorityWins(lineCount: number): number {
  return Math.floor(lineCount / 2) + 1;
}

/** Bloque completo a partir de uno parcial (p. ej. `{enabled: true}` escrito a
 * mano en la categoría): lo que falte se toma de la fórmula Mini Copa. Sin
 * esto, `block.lines.length` tronaba y dejaba el Paso 5 en blanco
 * (2026-09-16, smoke de Hermes). */
export function normalizeTeamPlayBlock(block: TeamPlayConfig | null | undefined): TeamPlayConfig {
  const base = defaultTeamPlay();
  if (!block || typeof block !== "object") return { ...base, enabled: false };
  // Solo se completa lo que FALTA: una lista vacía se conserva para que la
  // validación siga acusando "agrega al menos una línea".
  const lines = Array.isArray(block.lines)
    ? block.lines.filter((line) => line && typeof line === "object" && typeof line.kind === "string")
    : base.lines;
  const teamSize = Number.isInteger(block.team_size) ? block.team_size : base.team_size;
  const wins = Number.isInteger(block.wins_to_take_tie) ? block.wins_to_take_tie : majorityWins(lines.length);
  return {
    ...base,
    ...block,
    enabled: Boolean(block.enabled),
    team_size: teamSize,
    lines,
    wins_to_take_tie: wins,
    lineup_mode: block.lineup_mode ?? base.lineup_mode,
    default_score: typeof block.default_score === "string" && block.default_score.trim() ? block.default_score : base.default_score,
    default_counts_as_played: typeof block.default_counts_as_played === "boolean" ? block.default_counts_as_played : base.default_counts_as_played,
  };
}

/** "Equipos de 2 · 2 singles + 1 dobles · gana con 2". */
export function teamPlaySummary(raw: TeamPlayConfig | null | undefined): string | null {
  if (!teamPlayEnabled(raw)) return null;
  const block = normalizeTeamPlayBlock(raw);
  const counts = new Map<TeamPlayLineKind, number>();
  for (const line of block.lines) counts.set(line.kind, (counts.get(line.kind) ?? 0) + 1);
  const parts = (["singles", "doubles", "mixed"] as TeamPlayLineKind[])
    .filter((kind) => counts.get(kind))
    .map((kind) => `${counts.get(kind)} ${LINE_KIND_LABELS[kind].toLowerCase()}`);
  return `Equipos de ${block.team_size} · ${parts.join(" + ")} · gana con ${block.wins_to_take_tie}`;
}

/** Errores que el admin puede corregir antes de guardar (el backend repite
 * la validación). Null cuando todo está bien. */
export function teamPlayValidationError(raw: TeamPlayConfig | null | undefined): string | null {
  if (!teamPlayEnabled(raw)) return null;
  const block = normalizeTeamPlayBlock(raw);
  if (!Number.isInteger(block.team_size) || block.team_size < TEAM_SIZE_MIN || block.team_size > TEAM_SIZE_MAX) {
    return `El equipo debe tener entre ${TEAM_SIZE_MIN} y ${TEAM_SIZE_MAX} jugadores.`;
  }
  if (block.lines.length === 0) return "Agrega al menos una línea a la serie.";
  if (block.lines.length > LINES_MAX) return `Máximo ${LINES_MAX} líneas por serie.`;
  if (
    !Number.isInteger(block.wins_to_take_tie)
    || block.wins_to_take_tie < 1
    || block.wins_to_take_tie > block.lines.length
  ) {
    return `Las victorias para ganar la serie deben estar entre 1 y ${block.lines.length}.`;
  }
  if (!/^\s*\d+\s*[-/:]\s*\d+(\s+\d+\s*[-/:]\s*\d+)*\s*$/.test(block.default_score)) {
    return "El marcador por default debe verse como 6-2 6-2.";
  }
  return null;
}
